import {toast} from 'react-toastify'

import {
  ButtonIconEnum,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '@sdox/api'
import {
  IconAdmin,
  IconArrowLeft,
  IconCar,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconClose,
  IconCo2,
  IconCo2Large,
  IconContentManagement,
  IconCopy,
  IconDocument,
  IconDownload,
  IconDragHorizontal,
  IconEfficiency,
  IconElectric,
  IconExpand,
  IconExternalLink,
  IconEye,
  IconFilter,
  IconGas,
  IconGasLarge,
  IconInfo,
  IconInfoCircle,
  IconInfoCircleFilled,
  IconLeadGenerator,
  IconMail,
  IconMinus,
  IconOverflow,
  IconPhone,
  IconPlaceholder,
  IconPlay,
  IconPlus,
  IconSales,
  IconSearch,
  IconShare,
  IconSpinner,
  IconSupport,
  IconUser,
  IconWarning,
} from '@sdox/icons'
import {
  IconEfficiencyLarge,
  IconElectricLarge,
  IconExternalLinkLarge,
} from '@sdox/icons/large'

import {Panel, usePanelStore} from '../../stores'

import type {TrackingProps} from '@sdox/utils/dom'
import type {ButtonInterfaceFragment} from '@sdox/api'
import type {
  ButtonProps,
  ButtonSize,
  ButtonVariant,
  LinkButtonProps,
} from '@sdox/ui'
import type {ReactNode} from 'react'

export type GenericButtonProps = ButtonInterfaceFragment

export const sizeMap: Record<ButtonSizeEnum, ButtonSize> = {
  [ButtonSizeEnum.Large]: 'large',
  [ButtonSizeEnum.Medium]: 'medium',
  [ButtonSizeEnum.Small]: 'small',
}

export const variantMap: Record<ButtonVariantEnum, ButtonVariant> = {
  [ButtonVariantEnum.Filled]: 'filled',
  [ButtonVariantEnum.FilledBrand]: 'filled-brand',
  [ButtonVariantEnum.FilledInversed]: 'filled-inversed',
  [ButtonVariantEnum.Outline]: 'outline',
  [ButtonVariantEnum.OutlineInversed]: 'outline-inversed',
  [ButtonVariantEnum.Overlay]: 'overlay',
  [ButtonVariantEnum.OverlayBrand]: 'overlay-brand',
  [ButtonVariantEnum.OverlayInversed]: 'overlay-inversed',
  [ButtonVariantEnum.Flat]: 'flat',
  [ButtonVariantEnum.Secondary]: 'flat',
}

const iconMap: Record<ButtonIconEnum, ReactNode> = {
  [ButtonIconEnum.Co2]: <IconCo2 />,
  [ButtonIconEnum.Co2Large]: <IconCo2Large />,
  [ButtonIconEnum.Admin]: <IconAdmin />,
  [ButtonIconEnum.ArrowLeft]: <IconArrowLeft />,
  [ButtonIconEnum.Car]: <IconCar />,
  [ButtonIconEnum.ChevronDown]: <IconChevronDown />,
  [ButtonIconEnum.ChevronLeft]: <IconChevronLeft />,
  [ButtonIconEnum.ChevronRight]: <IconChevronRight />,
  [ButtonIconEnum.ChevronUp]: <IconChevronUp />,
  [ButtonIconEnum.Close]: <IconClose />,
  [ButtonIconEnum.ContentManagement]: <IconContentManagement />,
  [ButtonIconEnum.Copy]: <IconCopy />,
  [ButtonIconEnum.Document]: <IconDocument />,
  [ButtonIconEnum.Download]: <IconDownload />,
  [ButtonIconEnum.DragHorizontal]: <IconDragHorizontal />,
  [ButtonIconEnum.Efficiency]: <IconEfficiency />,
  [ButtonIconEnum.EfficiencyLarge]: <IconEfficiencyLarge />,
  [ButtonIconEnum.Electric]: <IconElectric />,
  [ButtonIconEnum.ElectricLarge]: <IconElectricLarge />,
  [ButtonIconEnum.Expand]: <IconExpand />,
  [ButtonIconEnum.ExternalLink]: <IconExternalLink />,
  [ButtonIconEnum.ExternalLinkLarge]: <IconExternalLinkLarge />,
  [ButtonIconEnum.Eye]: <IconEye />,
  [ButtonIconEnum.Filter]: <IconFilter />,
  [ButtonIconEnum.Gas]: <IconGas />,
  [ButtonIconEnum.GasLarge]: <IconGasLarge />,
  [ButtonIconEnum.Info]: <IconInfo />,
  [ButtonIconEnum.InfoCircle]: <IconInfoCircle />,
  [ButtonIconEnum.InfoCircleFilled]: <IconInfoCircleFilled />,
  [ButtonIconEnum.LeadGenerator]: <IconLeadGenerator />,
  [ButtonIconEnum.Mail]: <IconMail />,
  [ButtonIconEnum.Minus]: <IconMinus />,
  [ButtonIconEnum.Overflow]: <IconOverflow />,
  [ButtonIconEnum.Phone]: <IconPhone />,
  [ButtonIconEnum.Placeholder]: <IconPlaceholder />,
  [ButtonIconEnum.Play]: <IconPlay />,
  [ButtonIconEnum.Plus]: <IconPlus />,
  [ButtonIconEnum.Sales]: <IconSales />,
  [ButtonIconEnum.Search]: <IconSearch />,
  [ButtonIconEnum.Share]: <IconShare />,
  [ButtonIconEnum.Spinner]: <IconSpinner />,
  [ButtonIconEnum.Support]: <IconSupport />,
  [ButtonIconEnum.User]: <IconUser />,
  [ButtonIconEnum.Warning]: <IconWarning />,
}

export const getIcon = (icon: ButtonIconEnum | null): ReactNode => {
  if (icon === null) return null
  return iconMap[icon]
}

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

export const useGenericButtonProps = (props: GenericButtonProps | undefined) => {
  const setShowPanel = usePanelStore((state) => state.setShowPanel)

  if (!props) return {}

  if (props.__typename === 'HrefButtonType') {
    return {
      href: props.href,
      mboAttrs: props.mboAttrs as TrackingProps,
      size: sizeMap[props.size],
      variant: variantMap[props.variant],
      leading: getIcon(props.leadingIcon),
      trailing: getIcon(props.trailingIcon) ?? <IconExternalLink />,
      onClick: () => window.open(props.href, '_blank')?.focus(),
      children: props.label,
    } satisfies LinkButtonProps
  } else if (props.__typename === 'DownloadButtonType') {
    return {
      href: props.media.fileUrl,
      mboAttrs: props.mboAttrs as TrackingProps,
      size: sizeMap[props.size],
      variant: variantMap[props.variant],
      leading: getIcon(props.leadingIcon),
      trailing: getIcon(props.trailingIcon),
      download: props.fileName ?? 'Anhang',
      children: props.label,
    } satisfies LinkButtonProps
  } else if (props.__typename === 'CopyButtonType') {
    return {
      size: sizeMap[props.size],
      variant: variantMap[props.variant],
      // CopyButton does not allow any other leading or trailing icons
      trailing: <IconCopy />,
      onClick: async () => {
        if (await copyToClipboard(props.content)) {
          toast.success('Copied to clipboard')
        }
      },
      children: props.label,
      mboAttrs: props.mboAttrs as TrackingProps,
    } satisfies ButtonProps
  } else if (props.__typename === 'ContactFormTriggerButtonType') {
    return {
      leading: getIcon(props.leadingIcon),
      trailing: getIcon(props.trailingIcon),
      onClick: () => setShowPanel(Panel.CLAIM),
      children: props.label,
      mboAttrs: props.mboAttrs as TrackingProps,
    } satisfies ButtonProps
  }
  return props
}
